import React, { useEffect } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { observer } from "mobx-react"
import { IAsset, ILeaderboardAsset } from "stores/main-store.types"
import MainStore from "stores/main-store"
import { EnhancedTable, IHeadCell } from "components/common/enhanced-table"
import { Badge, Dialog, DialogContent, Tooltip, useMediaQuery } from "@mui/material"
import capitalize from "@mui/utils/capitalize"
import userStore from "stores/user-store"
import alertsStore from "stores/alerts-store"
import { SentimentAnalysisBadge } from "components/common/sentiment-analysis-badge"
import { Close, FilterAlt, FilterAltOutlined } from "@mui/icons-material"
import { DialogTopIconButton } from "../../components/common/app-controls/dialog-top-icon-button"
import { PageLogo } from "../../components/common/page-logo"
import { AppButton } from "../../components/common/app-controls/app-button"
import { useTheme } from "@mui/material/styles"
import { AppTextField } from "../../components/common/app-controls/app-text-field"

const AddNotificationWhiteIcon = require("assets/icons/add-notification-white.svg")

const headCells = (setFilterModalOpen, filterText): IHeadCell[] => [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: (
      <Box display="flex">
        <Tooltip title="Filter by text">
          <Box position="relative" top={4} left={-5} onClick={(e) => {
            setFilterModalOpen(true)
            e.stopPropagation()
          }}>
            {filterText ? <FilterAlt fontSize="small" /> : <FilterAltOutlined fontSize="small" />}
          </Box>
        </Tooltip>
        {"Asset"}
      </Box>
    ),
    displayOnMobile: false,
    align: "left",
    width: "35%",
    blurCell: (row: ILeaderboardAsset) => row.isPro && !userStore.currentUserData?.isProUser
  },
  {
    id: "symbol",
    numeric: true,
    disablePadding: false,
    label: "symbol",
    width: "15%",
    blurCell: (row: ILeaderboardAsset) => row.isPro && !userStore.currentUserData?.isProUser
  },
  {
    id: "hype",
    numeric: true,
    disablePadding: false,
    label: "Hype %",
    info: "daily change",
    width: "15%",
    renderValue: (row: ILeaderboardAsset) => {
      let color = "default"

      if (row.sentiment !== 0) {
        color = row.sentiment > 0 ? "success.main" : "error.main"
      }

      return row.hype >= 0 ? (
        <Typography fontSize="inherit" color={color} fontWeight="300">+ {row.hype}%</Typography>
      ) : (
        <Typography fontSize="inherit" color={color} fontWeight="300">- {0 - (row.hype as number)}%</Typography>
      )
    }
  },
  {
    id: "price",
    numeric: true,
    disablePadding: false,
    label: "Price %",
    info: "daily change",
    width: "15%",
    renderValue: (row: ILeaderboardAsset) => (
      isNaN(row.price) ? (
        <Typography fontSize="inherit" color="text.secondary" fontWeight="300">No data</Typography>
      ) : (
        row.price >= 0 ? (
          <Typography fontSize="inherit" color="success.main" fontWeight="300">+ {row.price}%</Typography>
        ) : (
          <Typography fontSize="inherit" color="error.main" fontWeight="300">- {0 - (row.price as number)}%</Typography>
        )
      )
    )
  },
  {
    id: "exchange",
    numeric: true,
    disablePadding: false,
    label: "Exchange",
    displayOnMobile: false,
    width: "15%",
  },
  {
    id: "sentiment",
    numeric: true,
    disablePadding: false,
    label: "Sentiment",
    displayOnMobile: true,
    overflow: "visible",
    width: "5%",
    align: "center",
    renderValue: (row: ILeaderboardAsset) => <SentimentAnalysisBadge sentimentString={row.sentimentString} />
  },
  {
    id: "alert",
    numeric: true,
    disablePadding: false,
    label: "Alert",
    displayOnMobile: false,
    width: "0%",
    display: Boolean(userStore.currentUserData?.isProUser),
    renderValue: (row: ILeaderboardAsset) => {
      const assetRowHasAlerts = alertsStore.allUserAlerts.some(a => a.assetId === row.id)
      return (
        <Tooltip title="Add alert" PopperProps={{sx: { pointerEvents: "none" } }}>
          <Badge
            badgeContent={<img src={AddNotificationWhiteIcon} alt="alert" width="100%" />}
            color="default"
            sx={theme => ({
              "& .MuiBadge-badge": {
                background: assetRowHasAlerts ? theme.palette.primary.main : theme.palette.text.disabled,
                width: 25,
                height: 25,
                borderRadius: "50%",
                padding: "6px",
                pointerEvents: "all",
                cursor: "pointer",
                "&:hover": { background: theme.palette.primary.main }
              }
            })}
            onClick={(e) => {
              e.stopPropagation()
              alertsStore.setAddAlertPopup({open: true, selectedAsset: row.id})
            }}
          />
        </Tooltip>
      )
    }
  }
]

export const LeaderboardTable = observer(({ leaderboardAssets }: { leaderboardAssets: ILeaderboardAsset[] }) => {
  const navigate = useNavigate()
  const [ mainStore ] = useState(() => MainStore)
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down("sm"))
  const [ filteredAssets, setFilteredAssets ] = useState([])
  const [ filterText, setFilterText ] = useState("")
  const [ filterTextError, setFilterTextError ] = useState("")
  const [ filterModalOpen, setFilterModalOpen ] = useState(false)

  useEffect(() => {
    if (leaderboardAssets.length) setFilteredAssets(leaderboardAssets)
  }, [ leaderboardAssets ])

  const assetTypeFilter = (asset: IAsset) => {
    if (mainStore.assetType === "all") return true
    if (mainStore.assetType === "crypto") return asset.exchange === "CRYPTO"
    return asset.exchange !== "CRYPTO"
  }

  const onSubmitFilterClick = () => {
    const filtered = leaderboardAssets.filter(a =>
      JSON.stringify(a).toLowerCase().includes(filterText.toLowerCase()))
    if (filtered.length) {
      setFilteredAssets(filtered)
      setFilterModalOpen(false)
    } else {
      setFilterTextError("There are no results on your filter")
    }
  }

  return (
    <Box width={1} overflow="visible" mb={4}>
      <EnhancedTable
        tableCells={headCells(setFilterModalOpen, filterText)}
        tableData={filteredAssets}
        filter={assetTypeFilter}
        orderByDefault="hype|desc"
        onRowClick={(row) => navigate(`/hype/${row.id}`)}
      />

      <Dialog open={filterModalOpen} onClose={() => setFilterModalOpen(false)} PaperProps={{ sx: { borderRadius: "60px" } }} BackdropProps={{ sx: { transition: "none !important" }}} fullWidth>
        <DialogContent>
          <Box width={1} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <Box position="absolute" top={15} right={10}>
              <DialogTopIconButton position="right" tooltipTitle="Close" icon={<Close />} onClick={() => setFilterModalOpen(false)} />
            </Box>

            {/*<PageLogo />*/}

            <Typography variant="h6" width={1} align="center" fontWeight="700" mt={7} fontSize={sm ? 15 : 20}>
              Filter Leaderboard Assets
            </Typography>

            <AppTextField
              value={filterText}
              onChange={(value) => {
                setFilterText(value)
                setFilterTextError("")
              }}
              onKeyUp={e => {
                if (e.key === "Enter") onSubmitFilterClick()
              }}
              label="Filter (free text)"
              placeholder="Enter free text"
              error={filterTextError}
            />

            <AppButton
              onClick={onSubmitFilterClick}
              className={filterTextError ? "animate-shake" : undefined}
            >
              OK
            </AppButton>

            <Box mt={sm ? 2 : 3} sx={{cursor: "pointer"}} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
})
